import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { input, btnGhost } from "./ui";
import { FileDown, Trash2, Loader2, TriangleAlert } from "lucide-react";

export default function DeletePatientModal({ patient, onClose, onDeleted }) {
  const [confirmText, setConfirmText] = useState("");
  const [downloaded, setDownloaded] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (!patient) return null;
  const nameOk = confirmText.trim().toLowerCase() === (patient.name || "").trim().toLowerCase();
  const canDelete = nameOk && downloaded && !deleting;

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get(`/patients/${patient.id}/archive-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `historico-${patient.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setDownloaded(true);
      toast.success("PDF do histórico baixado. Guarde-o em local seguro.");
    } catch {
      toast.error("Não consegui gerar o PDF. Tente novamente.");
    } finally {
      setDownloading(false);
    }
  };

  const doDelete = async () => {
    if (!canDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/patients/${patient.id}`);
      toast.success("Paciente e todos os dados apagados definitivamente.");
      onDeleted();
    } catch {
      toast.error("Não consegui apagar. Tente novamente.");
      setDeleting(false);
    }
  };

  return (
    <Dialog open={!!patient} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#FDFBF7]" data-testid="delete-patient-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#2C332A]">Apagar {patient.name}?</DialogTitle>
          <DialogDescription className="sr-only">Confirmação de exclusão definitiva do paciente e de todos os dados</DialogDescription>
        </DialogHeader>

        <div className="rounded-xl bg-[#C87A5E]/10 border border-[#C87A5E]/25 p-4 flex gap-3" data-testid="delete-warning">
          <TriangleAlert className="w-5 h-5 text-[#C87A5E] shrink-0 mt-0.5" />
          <div className="font-body text-sm text-[#2C332A]">
            <p className="font-medium">Esta ação é permanente e não pode ser desfeita.</p>
            <p className="mt-1 text-[#4A5D4E]">Serão apagados para sempre: cadastro, prontuário e evoluções, dados clínicos, anexos, sessões e histórico de pagamentos deste paciente.</p>
          </div>
        </div>

        <div className="space-y-4 mt-2">
          <div>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-[#7A8A7A] mb-2">Passo 1 · Guarde o histórico</p>
            <button onClick={downloadPdf} disabled={downloading} data-testid="download-patient-archive"
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm transition-colors ${downloaded ? "border-[#5B6E5B] bg-[#5B6E5B]/10 text-[#4A5D4E]" : "border-[#5B6E5B]/30 text-[#5B6E5B] hover:bg-white"}`}>
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              {downloaded ? "PDF baixado ✓ (baixar novamente)" : "Baixar histórico completo em PDF"}
            </button>
          </div>

          <div>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-[#7A8A7A] mb-2">Passo 2 · Confirme</p>
            <p className="font-body text-sm text-[#4A5D4E] mb-2">Digite o nome <strong>{patient.name}</strong> para confirmar:</p>
            <input className={input} value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
              placeholder={patient.name} data-testid="delete-confirm-name" autoComplete="off" />
          </div>

          <div className="flex justify-end gap-3 pt-1">
            <button type="button" className={btnGhost} onClick={onClose} data-testid="delete-cancel">Cancelar</button>
            <button onClick={doDelete} disabled={!canDelete} data-testid="confirm-delete-patient"
              title={!downloaded ? "Baixe o PDF do histórico primeiro" : !nameOk ? "Digite o nome do paciente" : "Apagar definitivamente"}
              className="inline-flex items-center gap-2 rounded-full bg-[#C87A5E] px-5 py-2.5 text-sm text-[#FDFBF7] hover:bg-[#B56A50] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Apagar tudo definitivamente
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
